<template>
  <div class="confirm">
    <!-- 图标 + 标题 + 内容 合并，距左 20px 距顶 48px -->
    <div class="confirm__body">
      <div class="confirm__header">
        <div class="confirm__icon">!</div>
        <div class="confirm__title">{{ props.title }}</div>
      </div>
      <div class="confirm__content">{{ props.content }}</div>
    </div>

    <!-- 按钮容器 -->
    <div class="confirm__actions">
      <!-- 取消按钮 -->
      <Button variant="back" @click="handleCancel">
        {{ props.cancelText }}
      </Button>

      <!-- 确认按钮 -->
      <Button variant="danger" @click="handleOk">
        {{ props.okText }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import Button from '@rendora/design/components/button/Button.vue';

export interface ConfirmProps {
  title: string;
  content: string;
  okText: string;
  cancelText: string;
  onOk?: () => void | Promise<void>;
  onCancel?: () => void;
  type?: 'warning' | 'info' | 'success' | 'error';
  onClose?: () => void;
}

const props = withDefaults(defineProps<ConfirmProps>(), {
  type: 'warning',
});

const emit = defineEmits<{
  ok: [];
  cancel: [];
}>();

const handleCancel = () => {
  emit('cancel');
  props.onCancel?.();
  props.onClose?.();
};

const handleOk = async () => {
  try {
    await props.onOk?.();
    emit('ok');
    props.onClose?.();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Confirm action failed:', error);
    // 即使出错也触发 ok 事件并关闭
    emit('ok');
    props.onClose?.();
  }
};
</script>

<style scoped lang="scss">
@use '@rendora/design/styles/mixins.scss' as *;

.confirm {
  padding: 20px;
  text-align: left;

  @include mobile {
    padding: responsive-size(24px);
  }
}

.confirm__body {
  margin-top: 28px;

  @include mobile {
    margin-top: responsive-size(28px);
  }
}

.confirm__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;

  @include mobile {
    gap: responsive-size(12px);
    margin-bottom: responsive-size(10px);
  }
}

.confirm__icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  background-color: var(--x-warning-6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: var(--x-text-14-size);
  font-weight: bold;

  @include mobile {
    width: responsive-size(24px);
    height: responsive-size(24px);
  }
}

.confirm__title {
  font-size: var(--x-text-16-size);
  font-weight: bold;
  color: var(--x-text-1);
  line-height: var(--x-text-16-line-height);

  @include mobile {
    font-size: responsive-size(var(--x-text-16-size));
  }
}

.confirm__content {
  font-size: var(--x-text-14-size);
  line-height: var(--x-text-14-line-height);
  color: var(--x-text-5);
  margin-bottom: 40px;
  padding-left: 36px;

  @include mobile {
    margin-bottom: responsive-size(40px);
    padding-left: responsive-size(36px);
  }
}

.confirm__actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  @include mobile {
    gap: responsive-size(12px);
  }
}
</style>
